import { useEffect, useRef } from 'react';
import { SearchSelect } from './search-select.js';

// #28: Das Panel-Gegenstueck zu CatalogEditor. Anlege- und Detail-Ansicht rendern aus DERSELBEN
// Feldliste — ein Feld hinzufuegen heisst genau eine Zeile in der Liste, es gibt keine zweite
// Stelle, die nachgezogen werden muesste (Spec Kapitel 2.1). Der zugehoerige Guard-Test stellt
// die Liste gegen das Zod-Create-Schema.

export type PanelField<TDraft> = { key: string; labelKey: string } & (
  | {
      kind: 'text' | 'textarea';
      get: (d: TDraft) => string;
      set: (d: TDraft, v: string) => TDraft;
    }
  | {
      kind: 'checkbox';
      get: (d: TDraft) => boolean;
      set: (d: TDraft, v: boolean) => TDraft;
      /** Erklaerzeile unter dem Haekchen — dort, wo der Name allein mehrdeutig waere. */
      hintKey?: string;
    }
  | {
      kind: 'select';
      get: (d: TDraft) => string | null;
      set: (d: TDraft, v: string | null) => TDraft;
      options: { value: string; label: string }[];
      /** Ohne allowEmpty keine Leeroption — ein Pflichtfeld soll keinen Zustand anbieten,
       *  den es fachlich nicht gibt (Lehre aus #52). */
      allowEmpty?: boolean;
      emptyLabel?: string;
      /** #207: Suchfeld statt <select> — für Nutzer-Auswahlen mit langer Liste. */
      searchable?: boolean;
    }
);

export function PanelFieldInput<TDraft>({
  field,
  draft,
  onChange,
  autoFocus = false,
  invalid = false,
}: {
  field: PanelField<TDraft>;
  draft: TDraft;
  onChange: (next: TDraft) => void;
  autoFocus?: boolean;
  invalid?: boolean;
}) {
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  const cls = `w-full rounded-btn border bg-bg-surface px-2 py-1.5 text-sm text-text-body ${
    invalid ? 'border-status-fail' : 'border-[var(--border)]'
  }`;

  if (field.kind === 'checkbox') {
    return (
      <input
        ref={ref as React.Ref<HTMLInputElement>}
        type="checkbox"
        aria-label={field.labelKey}
        checked={field.get(draft)}
        onChange={(e) => onChange(field.set(draft, e.target.checked))}
        className="h-4 w-4"
      />
    );
  }

  if (field.kind === 'select' && field.searchable) {
    return (
      <SearchSelect
        label={field.labelKey}
        value={field.get(draft) ?? ''}
        onChange={(v) => onChange(field.set(draft, v || null))}
        options={field.options}
        {...(field.allowEmpty ? { emptyLabel: field.emptyLabel ?? '—' } : {})}
        className={cls}
      />
    );
  }

  if (field.kind === 'select') {
    return (
      <select
        ref={ref as React.Ref<HTMLSelectElement>}
        aria-label={field.labelKey}
        value={field.get(draft) ?? ''}
        onChange={(e) => onChange(field.set(draft, e.target.value || null))}
        className={cls}
      >
        {field.allowEmpty && <option value="">{field.emptyLabel ?? '—'}</option>}
        {field.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  if (field.kind === 'textarea') {
    return (
      <textarea
        ref={ref as React.Ref<HTMLTextAreaElement>}
        aria-label={field.labelKey}
        rows={3}
        value={field.get(draft)}
        onChange={(e) => onChange(field.set(draft, e.target.value))}
        className={cls}
      />
    );
  }

  return (
    <input
      ref={ref as React.Ref<HTMLInputElement>}
      type="text"
      aria-label={field.labelKey}
      value={field.get(draft)}
      onChange={(e) => onChange(field.set(draft, e.target.value))}
      className={cls}
    />
  );
}
